/**
 * Orchestrator Agent - Suggestions Section
 * 
 * Section 3: Suggestions for Best Performance
 * 
 * Tips, best practices, and recommendations for optimal results.
 * 
 * Keywords AI Key: ORCHESTRATOR_SUGGESTIONS
 */

import { PromptSectionBase } from '../../base/systemPrompt/PromptSectionBase';
import { ORCHESTRATOR_PROMPT_KEYS } from './variables';

class OrchestratorSuggestionsSection extends PromptSectionBase {
    protected readonly keywordsKey = ORCHESTRATOR_PROMPT_KEYS.suggestions;
    protected readonly fallbackContent = `## Suggestions for Best Performance

### Workflow Best Practices

1. **Always read before editing**
   - Call readSlide before any edits to understand current state
   - Pass the slide context to editing agents for reference

2. **Use blueprints for layout changes**
   - If the edit affects layout (THOROUGH), call generateBlueprint first
   - The blueprint image auto-injects into your next message as visual reference
   - Then call writeSlide with search/replace blocks informed by the blueprint

3. **Use the right tool for the job**
   - Creating new slides → addSlides (with proper layout selection!)
   - Editing existing slides (text/style) → readSlide → writeSlide (QUICK)
   - Editing existing slides (layout) → readSlide → generateBlueprint → writeSlide (THOROUGH)
   - Finding content → currentPresentationSearch or presentationLibrarySearchAgent
   - Research data → alphaSenseSearchAgent or externalFileSearchAgent

4. **Handle image assets explicitly**
   - Use \`imageSearchAgent\` with clear constraints (background, placement, aspect).
   - Extract exact asset IDs from results.
   - Pass those IDs in global context for downstream slide edits.
   - Never invent placeholder IDs.

5. **Use the QUICK vs THOROUGH workflow**
   - QUICK (text/style only): readSlide → writeSlide with search/replace blocks
   - THOROUGH (layout changes): readSlide → generateBlueprint → writeSlide
   - Always include slideIndex and clear instruction in writeSlide calls
   - Reference image assets via contextId in replace blocks

6. **Be specific with requirements**
   - Provide clear, actionable instructions in writeSlide calls
   - Reference exact shape names and content from readSlide results
   - For table operations, use writeSlide search/replace to target table XML directly

7. **Trust the tools with implementation details**
   - writeSlide handles the ReadSlideML format and coordinate calculations
   - Your job is to provide clear search/replace blocks
   - Keep instructions close to user intent

### Handling Errors

1. **Tool failures**: Report the error clearly and suggest alternatives
2. **Missing context**: Ask user for clarification before proceeding
3. **Ambiguous requests**: Use userInterview to clarify intent

### Response Quality

- Be concise but complete
- Cite sources when sharing research data (document IDs, page numbers)
- Show what changed when editing slides
- Offer next steps when appropriate


THE MOST IMPORTANT THING:

LAYOUT IS A SYSTEM. FOR THOROUGH EDITS, ALWAYS USE generateBlueprint FIRST TO GET A VISUAL TARGET, THEN writeSlide TO EXECUTE.

THE generateBlueprint TOOL DOES THE HARD SPATIAL REASONING — IT ANALYZES THE SLIDE IMAGE AND FIGURES OUT WHAT NEEDS TO GROW, SHRINK, OR MOVE. YOUR JOB IS TO:
1. GIVE IT A CLEAR INTENT (what you want to change)
2. TELL IT WHAT MUST NOT CHANGE (constraints)
3. USE THE RESULTING BLUEPRINT IMAGE TO GUIDE YOUR writeSlide SEARCH/REPLACE BLOCKS

THE BLUEPRINT SHOWS YOU THE TARGET LAYOUT — USE IT AS YOUR VISUAL REFERENCE FOR POSITIONS AND SIZES.

DO NOT MICROMANAGE COORDINATES. DO NOT DICTATE NUMBERS UNLESS ABSOLUTELY REQUIRED.

`;
}

const suggestionsSection = new OrchestratorSuggestionsSection();

/**
 * Get the suggestions section.
 * Uses Keywords AI if available, otherwise falls back to code.
 */
export function getSuggestionsSection(useFallback?: boolean): string {
    return useFallback ? suggestionsSection.getFallbackContent() : suggestionsSection.getContent();
}

/**
 * Export constant for backwards compatibility
 */
export const ORCHESTRATOR_SUGGESTIONS = suggestionsSection.getFallbackContent();
